/* sidda:b2b — trade mode client.
 *
 * Only ever loaded for a signed-in customer tagged "B2B APPROVED" (the gate is
 * in sd-b2b.liquid, server rendered, so the browser cannot fake its way in).
 *
 * WHAT THIS FILE IS ALLOWED TO DO
 *   Rewrite DISPLAYED prices to trade ex GST, and swap the checkout button for
 *   a PO builder.
 *
 * WHAT IT MUST NEVER DO
 *   Decide what anything costs. Every figure that becomes money is computed
 *   server side by the b2b app from the live Shopify price and the account's
 *   tier. If this file failed to load, a trade customer would see retail prices
 *   and their PO would still be priced correctly. Keep that property.
 *
 * The discount is fetched from /apps/b2b/api/session rather than read from
 * Liquid, so a stale customer metafield can never show a better price than the
 * one the account actually gets.
 */
(function () {
  'use strict';

  var CFG = window.SD_B2B;
  if (!CFG || !CFG.on) return;

  var EP = CFG.endpoint || '/apps/b2b';
  var GST = 1 + (CFG.gstRate || 0.1);
  var session = null;

  // Nodes we have already rewritten. A WeakSet means re-running on DOM changes
  // is cheap and can never double-discount a price.
  var done = new WeakSet();

  // ---------- money ----------

  // Matches "$1,234.56" and "$1234" inside a text node.
  var MONEY_RE = /\$\s?\d[\d,]*(?:\.\d{2})?/;

  function parseMoney(s) {
    var m = String(s).match(MONEY_RE);
    if (!m) return null;
    var n = Number(m[0].replace(/[^0-9.]/g, ''));
    return isFinite(n) ? n : null;
  }

  function fmt(n) {
    return '$' + Number(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function tradeExGst(retailIncGst) {
    var incGst = retailIncGst * (1 - session.discount);
    return Math.round((incGst / GST) * 100) / 100;
  }

  // ---------- reseller price book ----------
  //
  // A reseller does not get a percentage off, they get a real price per SKU, so
  // the browser cannot derive it from what is on the page. The SERVER sends a
  // handle -> price map and this file only looks prices up in it. Anything not
  // in the map has no reseller price yet.
  var book = null;      // local stock prices,    { handle: { i: incGst, e: exGst } }
  var bookIntl = null;  // factory order prices, same shape
  var hidden = null;    // { handle: 1 } products a reseller never sees
  var hiddenNav = [];       // nav hrefs to remove for a reseller
  var hiddenNavLabels = []; // top-level menu labels to remove

  function onBook() { return !!(session && session.pricedOffBook); }

  // INTERNATIONAL MODE. A reseller can flip the whole site between local stock
  // and a factory order. It is a whole-order choice, never per line, because the
  // two cannot be mixed on one purchase order — so it lives in one place and the
  // page reloads on the flip rather than trying to repaint a half-changed cart.
  var INTL_KEY = 'sd-b2b-intl';
  function intlOn() {
    if (!onBook()) return false;
    try { return localStorage.getItem(INTL_KEY) === '1'; } catch (e) { return false; }
  }
  function setIntl(on) {
    try { localStorage.setItem(INTL_KEY, on ? '1' : '0'); } catch (e) { /* private mode */ }
    location.reload();
  }
  function activeBook() { return intlOn() ? bookIntl : book; }
  window.SD_B2B_INTL = intlOn;

  // ---------- the international basket ----------
  //
  // Shopify's own cart REFUSES a sold-out variant: /cart/add.js answers 422
  // "The product ... is already sold out" (confirmed on the live store). That is
  // correct for retail and exactly wrong for a factory order, where what is on
  // the shelf here says nothing about what the factory can make.
  //
  // So international mode keeps its own basket instead of Shopify's. Nothing is
  // lost by doing that: submission already takes a plain list of variant ids and
  // quantities and never reads the Shopify cart. It also makes the no-mixing
  // rule structural rather than a warning — the two baskets are separate stores,
  // so local stock cannot end up on an international order by accident.
  var BASKET_KEY = 'sd-b2b-intl-basket';

  function basketRead() {
    try {
      var raw = JSON.parse(localStorage.getItem(BASKET_KEY) || '[]');
      return Array.isArray(raw) ? raw.filter(function (l) { return l && l.variantId; }) : [];
    } catch (e) { return []; }
  }
  function basketWrite(lines) {
    try { localStorage.setItem(BASKET_KEY, JSON.stringify(lines)); } catch (e) { /* private mode */ }
    document.dispatchEvent(new CustomEvent('sd:b2b:intl-basket'));
    return lines;
  }
  function basketAdd(variantId, qty) {
    var lines = basketRead();
    var step = (session && session.intlQtyStep) || 10;
    var want = Math.max(step, Math.round((Number(qty) || step) / step) * step);
    var hit = lines.filter(function (l) { return String(l.variantId) === String(variantId); })[0];
    if (hit) hit.qty += want; else lines.push({ variantId: String(variantId), qty: want });
    return basketWrite(lines);
  }
  window.SD_B2B_BASKET = { read: basketRead, write: basketWrite, add: basketAdd, key: BASKET_KEY };

  // Add-to-cart goes to the international basket, and sold-out buttons come back
  // to life, because stock is not what decides whether a factory order is
  // possible. Capture phase so the theme's own handler never sees the click.
  function interceptAddToCart() {
    if (!onBook()) return;
    document.addEventListener('submit', function (ev) {
      if (!intlOn()) return;
      var form = ev.target;
      if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
      ev.preventDefault();
      ev.stopPropagation();
      var id = form.querySelector('[name="id"]');
      if (!id || !id.value) return;
      var qtyEl = form.querySelector('[name="quantity"]');
      basketAdd(id.value, qtyEl ? Number(qtyEl.value) : 0);
      intlToast('Added to your international order');
    }, true);
  }

  // Sold out is a local-stock fact. On a factory order it means nothing, so the
  // button says what it can actually do.
  function unlockSoldOut(root) {
    if (!onBook() || !intlOn()) return;
    var scope = root && root.querySelectorAll ? root : document;
    var btns = scope.querySelectorAll('form[action*="/cart/add"] button[disabled], form[action*="/cart/add"] [type="submit"][disabled]');
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (b.getAttribute('data-sd-b2b-unlocked') === '1') continue;
      b.setAttribute('data-sd-b2b-unlocked', '1');
      b.disabled = false;
      b.classList.add('sd-b2b-unlocked');
      var label = b.querySelector('span') || b;
      if (/sold out|unavailable/i.test(label.textContent || '')) label.textContent = 'Add to international order';
    }
  }

  var toastEl = null;
  function intlToast(text) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.id = 'sd-b2b-intl-toast';
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = text;
    toastEl.classList.add('on');
    clearTimeout(intlToast._t);
    intlToast._t = setTimeout(function () { toastEl.classList.remove('on'); }, 2400);
  }

  function loadBook() {
    if (!onBook()) return Promise.resolve(null);
    return fetch(EP + '/api/book', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (b) {
        book = (b && b.priced) || {};
        bookIntl = (b && b.pricedIntl) || {};
        hidden = {};
        ((b && b.hidden) || []).forEach(function (h) { hidden[h] = 1; });
        hiddenNav = (b && b.hiddenNav) || [];
        hiddenNavLabels = (b && b.hiddenNavLabels) || [];
        hideResellerNav();
        hideResellerCards(document);
        return book;
      })
      .catch(function (e) {
        // No book means we must not paint anything: retail stays on screen and
        // the trade cart, which is priced server side, remains the truth.
        console.warn('[sd-b2b] could not load the reseller price book, leaving retail prices on screen:', e.message);
        book = null;
        bookIntl = null;
        return null;
      });
  }

  // ---------- reseller announcement bar ----------
  //
  // Sidd 2026-08-13: a reseller account sees "SIDDAMENT RESELLER SITE" in the
  // top bar instead of the retail marquee. Done here rather than in
  // theme.liquid so the retail bar is never touched — one asset, no edit to the
  // layout, and it reverts by itself if the account lapses.
  //
  // The WHOLE bar is replaced, not just its contents.
  //
  // Two goes at this were wrong for the same reason: reusing the theme's ticker
  // markup. The retail bar carries the row twice (original + clone) because the
  // mobile ticker scrolls one into the other, so a short line rendered the words
  // twice on desktop; and .sd-marquee__item brings its own item styling, which
  // put a grey slab behind the bold word. Own markup, own classes, no track and
  // no group — nothing of the ticker survives to style or animate it.
  function resellerMarquee(root) {
    if (!onBook()) return;
    var scope = root && root.querySelectorAll ? root : document;
    var bars = scope.querySelectorAll('.sd-marquee');
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      if (bar.getAttribute('data-sd-b2b-reseller') === '1') continue;
      bar.setAttribute('data-sd-b2b-reseller', '1');
      bar.classList.add('sd-b2b-marquee-bar');
      bar.setAttribute('aria-label', 'Reseller account');
      var intl = intlOn();
      bar.innerHTML = '<div class="sd-b2b-bar">'
        + '<span class="sd-b2b-bar__rule"></span>'
        + '<span class="sd-b2b-bar__dot"></span>'
        + '<span class="sd-b2b-bar__txt">SIDDAMENT <em>RESELLER</em> SITE</span>'
        + '<span class="sd-b2b-switch" role="group" aria-label="Pricing mode">'
        +   '<button type="button" class="sd-b2b-switch__opt' + (intl ? '' : ' is-on') + '" data-sd-intl="0"'
        +     ' aria-pressed="' + (intl ? 'false' : 'true') + '">Local stock</button>'
        +   '<button type="button" class="sd-b2b-switch__opt' + (intl ? ' is-on' : '') + '" data-sd-intl="1"'
        +     ' aria-pressed="' + (intl ? 'true' : 'false') + '">International</button>'
        + '</span>'
        + '<span class="sd-b2b-bar__rule"></span>'
        + '</div>';

      bar.addEventListener('click', function (ev) {
        var opt = ev.target.closest && ev.target.closest('[data-sd-intl]');
        if (!opt) return;
        var want = opt.getAttribute('data-sd-intl') === '1';
        if (want === intlOn()) return;
        setIntl(want);
      });
    }
  }

  // ---------- hide what a reseller has no business seeing ----------
  //
  // Sidd 2026-08-13: printers, spare parts and the bought-in brands (ProtoPasta,
  // Prusa, the components range) come off the nav, the homepage and every grid.
  // This is NOT the same as having no reseller price — those items still show,
  // priced or marked. These are gone.
  //
  // Server-driven: the app sends the handles and the nav hrefs, so the rule
  // lives in data/reseller-prices.json rather than being scattered in CSS.
  function hideResellerNav() {
    if (!onBook()) return;

    // Top-level menus are <summary>, not <a>, so they have no href to match on.
    // Hide them by label. Doing this in CSS is impossible and doing it by href
    // is what took out the whole Filaments mega-menu on the first attempt: the
    // ProtoPasta collections live INSIDE Filaments, so li:has(a[...]) matched
    // the parent too. Sub-items below use a DIRECT-child selector for the same
    // reason.
    if (hiddenNavLabels.length) {
      var tops = document.querySelectorAll('.header__inline-menu > ul > li, .menu-drawer__navigation > ul > li');
      for (var i = 0; i < tops.length; i++) {
        var head = tops[i].querySelector('summary, a');
        if (!head) continue;
        var label = head.textContent.replace(/\s+/g, ' ').trim();
        for (var j = 0; j < hiddenNavLabels.length; j++) {
          if (label.toLowerCase() === String(hiddenNavLabels[j]).toLowerCase()) {
            tops[i].style.display = 'none';
            tops[i].setAttribute('data-sd-b2b-hidden-nav', '1');
          }
        }
      }
    }

    if (!hiddenNav.length) return;
    var css = hiddenNav.map(function (href) {
      var h = href.replace(/"/g, '');
      // Menu entries: DIRECT child only, or this swallows the parent mega-menu.
      // Homepage promo tiles wrap the link several divs deep, so those are
      // matched separately and scoped to the multicolumn list so the loose
      // descendant match cannot reach the nav.
      return 'li:has(> a[href*="' + h + '"])'
        + ', .multicolumn-list__item:has(a[href*="' + h + '"])'
        + ', .collage__item:has(a[href*="' + h + '"])';
    }).join(',');
    var st = document.getElementById('sd-b2b-hide-reseller');
    if (!st) {
      st = document.createElement('style');
      st.id = 'sd-b2b-hide-reseller';
      document.head.appendChild(st);
    }
    st.textContent = css + ' { display: none !important; }';
  }

  function hideResellerCards(root) {
    if (!onBook() || !hidden) return;
    var scope = root && root.querySelectorAll ? root : document;
    var links = scope.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < links.length; i++) {
      var m = links[i].getAttribute('href').match(/\/products\/([^/?#]+)/);
      if (!m || !hidden[m[1]]) continue;
      var card = links[i].closest('.card-wrapper, .grid__item, .card, .product-card, li');
      if (card && card.getAttribute('data-sd-b2b-hidden') !== '1') {
        card.setAttribute('data-sd-b2b-hidden', '1');
        card.style.display = 'none';
      }
    }
  }

  // Which product does this price element belong to? Only answers when it is
  // certain — a wrong handle would paint a wrong price, which is far worse than
  // painting nothing.
  function handleFor(el) {
    var card = el.closest('[data-product-handle], .card-wrapper, .card, .grid__item, .product-card');
    if (card) {
      var dh = card.getAttribute('data-product-handle');
      if (dh) return dh;
      var links = card.querySelectorAll('a[href*="/products/"]');
      var found = null;
      for (var i = 0; i < links.length; i++) {
        var m = links[i].getAttribute('href').match(/\/products\/([^/?#]+)/);
        if (!m) continue;
        if (found && found !== m[1]) return null; // card spans two products, do not guess
        found = m[1];
      }
      if (found) return found;
    }
    var pm = location.pathname.match(/^\/products\/([^/?#]+)/);
    return pm ? pm[1] : null;
  }

  // ---------- price rewriting ----------

  // Deliberately broad. Missing a price is a cosmetic bug; the PO is still
  // priced correctly server side. Anything marked data-sd-b2b-skip is left be.
  var PRICE_SELECTORS = [
    '.price-item', '.price__regular .price-item', '.price__sale .price-item',
    '.price', '.product__price', '.card__price',
    '[data-price]', '.money', '.cart-item__price', '.totals__subtotal-value',
    '.cart__price', '.sd-price'
  ].join(',');

  function rewriteNode(el) {
    if (!el || done.has(el)) return;
    if (el.closest('[data-sd-b2b-skip]')) return;

    // Only touch elements whose own text is a price, so we never mangle a
    // container that happens to hold one.
    var txt = el.textContent;
    if (!txt || !MONEY_RE.test(txt)) return;
    if (el.querySelector(PRICE_SELECTORS)) return; // it is a wrapper, let the child handle it

    var retail = parseMoney(txt);
    if (retail === null || retail <= 0) return;

    // Book-priced accounts: look the price up, never derive it.
    if (onBook()) {
      var active = activeBook();
      if (!active) return; // book unavailable, leave retail on screen
      var handle = handleFor(el);
      if (!handle) return; // cannot say which product this is, so say nothing
      done.add(el);
      el.setAttribute('data-sd-b2b-retail', String(retail));
      var entry = active[handle];
      if (!entry) {
        el.textContent = 'Reseller price soon';
        el.classList.add('sd-b2b-soon');
        return;
      }
      el.textContent = txt.replace(MONEY_RE, fmt(entry.e));
      var wasB = document.createElement('span');
      wasB.className = 'sd-b2b-was';
      wasB.textContent = fmt(retail);
      el.insertBefore(wasB, el.firstChild);
      var tagB = document.createElement('span');
      tagB.className = 'sd-b2b-ex';
      tagB.textContent = 'ex GST';
      el.appendChild(tagB);
      return;
    }

    done.add(el);
    el.setAttribute('data-sd-b2b-retail', String(retail));

    var trade = tradeExGst(retail);
    el.textContent = txt.replace(MONEY_RE, fmt(trade));

    // Struck-through retail ONLY when a discount actually exists. At 0% the
    // trade price is the same money as retail, just shown ex GST, so striking
    // the retail figure would imply a saving that is not there.
    if (session.discount > 0) {
      var was = document.createElement('span');
      was.className = 'sd-b2b-was';
      was.textContent = fmt(retail);
      el.insertBefore(was, el.firstChild);
    }

    var tag = document.createElement('span');
    tag.className = 'sd-b2b-ex';
    tag.textContent = 'ex GST';
    el.appendChild(tag);
  }

  function sweep(root) {
    if (!session) return;
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll(PRICE_SELECTORS);
    for (var i = 0; i < nodes.length; i++) rewriteNode(nodes[i]);
  }

  // The site has a custom infinite scroll, a cart drawer, swatch flyouts and
  // quick add, so prices appear long after first paint. Watch for them.
  function watch() {
    var pending = false;
    var mo = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        sweep(document);
        hideB2cLinks(document);
        resellerMarquee(document);
        unlockSoldOut(document);
        hideResellerCards(document);
      });
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });

    // Cart events fired by the theme and by the existing sd-cart-* widgets.
    ['sdwl:changed', 'cart:updated', 'cart:refresh', 'sd:cart:changed'].forEach(function (ev) {
      document.addEventListener(ev, function () { setTimeout(function () { sweep(document); }, 60); });
    });
  }

  // ---------- cart becomes a PO builder ----------
  //
  // Trade accounts never reach Shopify checkout, so every card, wallet and
  // express path is removed and replaced with three explicit actions:
  //
  //   Submit and order     -> live Net 30 order, invoice follows
  //   Submit and reserve   -> nothing is ordered, we come back to them
  //   Attach PO and submit -> their own PO document rides along with the order
  //
  // The PO number is required on all three. It is written to Shopify's NATIVE
  // order poNumber field, which is what puts it on their invoice.

  function cartJson() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  function priceCart(items) {
    return fetch(EP + '/api/price', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) { return r.json(); });
  }

  // Rewrites the cart total so BOTH figures are visible. The theme shows an
  // inc-GST "Estimated total" and a tax note claiming discounts are calculated
  // at checkout, which is wrong for an account that never reaches checkout.
  function rewriteCartTotals(totals) {
    if (!totals) return;
    // There is a .tax-note in the drawer as well as on the cart page. Rewrite
    // every one that is not inside the hidden drawer.
    var notes = document.querySelectorAll('.tax-note');
    for (var i = 0; i < notes.length; i++) {
      if (!inHiddenDrawer(notes[i])) { applyTaxNote(notes[i], totals); }
    }
  }

  function applyTaxNote(note, totals) {
    note.classList.add('sd-b2b-taxnote');
    note.innerHTML =
      '<span class="ex">' + fmt(totals.subtotalExGst) + ' ex GST</span>' +
      ' &nbsp;+&nbsp; ' + fmt(totals.gst) + ' GST' +
      ' &nbsp;=&nbsp; <span class="ex">' + fmt(totals.totalIncGst) + ' inc GST</span><br>' +
      '<span class="sd-b2b-note">Freight billed at cost. Retail shipping promotions do not apply to trade accounts.</span>';
  }

  // Strip every retail payment affordance out of the cart.
  function stripCheckout(root) {
    var kill = [
      '[name="checkout"]', '.cart__checkout-button', '#checkout', '#CartDrawer-Checkout',
      '.cart__dynamic-checkout-buttons', '.additional-checkout-buttons',
      '.shopify-payment-button', '.shopify-cleanslate', '[data-shopify="dynamic-checkout-cart"]',
      '.cart__ctas button', '.cart__ctas input[type="submit"]',
      // retail-only furniture that does not belong on a purchase order
      '.sd-wl-savecart', '.sd-src', '.sd-upsell'
    ];
    var scope = root && root.querySelectorAll ? root : document;
    kill.forEach(function (sel) {
      var nodes = scope.querySelectorAll(sel);
      Array.prototype.forEach.call(nodes, function (el) {
        if (el.closest && el.closest('.sd-b2b-po')) return; // never touch our own buttons
        el.style.setProperty('display', 'none', 'important');
        el.setAttribute('data-sd-b2b-hidden', '');
      });
    });
  }

  function buildPanel(cart, priced) {
    var wrap = document.createElement('div');
    wrap.className = 'sd-b2b-po';
    wrap.setAttribute('data-sd-b2b-skip', '');

    var t = priced.totals || {};
    var below = priced.belowMinimum;
    var short = priced.shortfalls || [];
    var lines = priced.lines || [];

    // Line items as a real table. A trade buyer checks quantities and unit
    // prices against their own PO before committing, so this has to be
    // scannable across the page, not a stack in a narrow column.
    var rows = lines.map(function (l, i) {
      var item = cart.items[i] || {};
      var img = l.image || item.image || '';
      return '<tr' + (l.short ? ' class="is-short"' : '') + '>' +
        '<td class="pi">' + (img ? '<img src="' + img + '" alt="" loading="lazy">' : '<span class="pi--none"></span>') + '</td>' +
        '<td class="pn"><div class="t">' + (l.title || item.product_title || 'Item') + '</div>' +
          '<div class="s">' + (l.sku || '') +
          (l.variantTitle ? ' &middot; ' + l.variantTitle : '') +
          (l.short ? ' &middot; <span class="short">only ' + Math.max(0, l.available) + ' in stock</span>' : '') +
          '</div></td>' +
        '<td class="n">' + l.qty + '</td>' +
        '<td class="n u-hide-sm">' + fmt(l.unitExGst) + '</td>' +
        '<td class="n b">' + fmt(l.lineExGst) + '</td>' +
        '</tr>';
    }).join('');

    wrap.innerHTML =
      '<div class="sd-b2b-head">' +
        '<h3>Purchase order</h3>' +
        '<div class="sd-b2b-sub">' + (priced.tier || 'Trade') + ' &middot; prices ex GST &middot; Net 30 from invoice date &middot; bank transfer only</div>' +
      '</div>' +

      '<div class="sd-b2b-grid">' +
        '<div class="sd-b2b-lines">' +
          '<table class="sd-b2b-tbl">' +
            '<colgroup><col class="c-img"><col><col class="c-qty"><col class="c-unit"><col class="c-line"></colgroup>' +
            '<thead><tr>' +
              '<th></th><th>Item</th><th class="n">Qty</th><th class="n u-hide-sm">Unit ex GST</th><th class="n">Line ex GST</th>' +
            '</tr></thead><tbody>' + rows + '</tbody></table>' +
        '</div>' +
        '<div class="sd-b2b-side">' +
          '<div class="sd-b2b-row"><span>Goods ex GST</span><span>' + fmt(t.goodsExGst || 0) + '</span></div>' +
          '<div class="sd-b2b-row"><span>Freight</span><span>billed at cost</span></div>' +
          '<div class="sd-b2b-row"><span>GST</span><span>' + fmt(t.gst || 0) + '</span></div>' +
          '<div class="sd-b2b-row total"><span>Total inc GST</span><span>' + fmt(t.totalIncGst || 0) + '</span></div>' +
        '</div>' +
      '</div>' +

      '<div class="sd-b2b-fields">' +
        '<div class="fld"><label for="sd-b2b-po-number">Your PO number <span class="sd-b2b-req">required</span></label>' +
          '<input id="sd-b2b-po-number" maxlength="60" required placeholder="Printed on your invoice"></div>' +
        '<div class="fld"><label for="sd-b2b-po-notes">Notes</label>' +
          '<input id="sd-b2b-po-notes" maxlength="2000" placeholder="Optional"></div>' +
        '<div class="fld"><label for="sd-b2b-po-file">Your PO document</label>' +
          '<input id="sd-b2b-po-file" type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.csv">' +
          '<div class="sd-b2b-filename" id="sd-b2b-filename"></div></div>' +
      '</div>' +

      (below
        ? '<div class="sd-b2b-msg bad">Trade orders start at ' + fmt(priced.minOrderExGst) + ' ex GST. Add more to submit.</div>'
        : '') +
      (short.length
        ? '<div class="sd-b2b-msg">' + short.length + ' line' + (short.length > 1 ? 's are' : ' is') +
          ' short on stock, so we will confirm before this becomes an order.</div>'
        : '') +

      '<div class="sd-b2b-actions">' +
        '<button type="button" data-mode="order"' + (below ? ' disabled' : '') + '>' +
          '<span class="t">Submit and order</span><span class="s">Net 30, invoice follows</span></button>' +
        '<button type="button" class="alt" data-mode="reserve"' + (below ? ' disabled' : '') + '>' +
          '<span class="t">Submit and reserve</span><span class="s">Nothing ordered, we will reach out</span></button>' +
        '<button type="button" class="alt" data-mode="upload"' + (below ? ' disabled' : '') + '>' +
          '<span class="t">Attach PO and submit</span><span class="s">Send your own PO with the order</span></button>' +
        '<button type="button" class="alt" data-mode="pdf">' +
          '<span class="t">Download PDF copy</span><span class="s">For your records, nothing submitted</span></button>' +
      '</div>' +

      '<div class="sd-b2b-msg" id="sd-b2b-result"></div>' +
      '<div class="sd-b2b-terms">Your PO number is printed on your invoice. You get your PO copy by email straight away; the tax invoice follows once freight is costed. Freight is charged at cost and retail shipping promotions do not apply to trade accounts.</div>';

    var fileInput = wrap.querySelector('#sd-b2b-po-file');
    var fileLabel = wrap.querySelector('#sd-b2b-filename');
    fileInput.addEventListener('change', function () {
      var f = fileInput.files && fileInput.files[0];
      fileLabel.textContent = f ? f.name + '  (' + Math.round(f.size / 1024) + 'KB)' : '';
    });

    Array.prototype.forEach.call(wrap.querySelectorAll('.sd-b2b-actions button'), function (btn) {
      btn.addEventListener('click', function () {
        var mode = btn.getAttribute('data-mode');
        if (mode === 'pdf') return downloadPoPdf(cart, wrap, btn);
        submitPo(cart, wrap, mode, btn);
      });
    });

    return wrap;
  }

  function readFileAsDataUrl(file) {
    return new Promise(function (resolve, reject) {
      var fr = new FileReader();
      fr.onload = function () { resolve(fr.result); };
      fr.onerror = function () { reject(new Error('Could not read that file.')); };
      fr.readAsDataURL(file);
    });
  }

  function submitPo(cart, wrap, mode, btn) {
    var out = wrap.querySelector('#sd-b2b-result');
    var ref = wrap.querySelector('#sd-b2b-po-number').value.trim();
    var fileInput = wrap.querySelector('#sd-b2b-po-file');
    var file = fileInput.files && fileInput.files[0];
    var buttons = wrap.querySelectorAll('.sd-b2b-actions button');

    function fail(msg, focusEl) {
      out.className = 'sd-b2b-msg bad';
      out.textContent = msg;
      if (focusEl) focusEl.focus();
    }

    if (!ref) return fail('A PO number is required.', wrap.querySelector('#sd-b2b-po-number'));
    if (mode === 'upload' && !file) return fail('Choose your PO document first, or use "Submit and order".', fileInput);

    Array.prototype.forEach.call(buttons, function (b) { b.disabled = true; });
    var originalLabel = btn.querySelector('.t').textContent;
    btn.querySelector('.t').textContent = 'Submitting';
    out.className = 'sd-b2b-msg';
    out.textContent = '';

    // 'upload' is 'order' with their document attached, so the server only ever
    // sees the two real modes.
    var wireMode = mode === 'reserve' ? 'reserve' : 'order';

    Promise.resolve(file && (mode === 'upload' || file) ? readFileAsDataUrl(file) : null)
      .then(function (dataUrl) {
        return fetch(EP + '/api/po/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: cart.items.map(function (i) { return { variantId: i.variant_id, qty: i.quantity }; }),
            poNumber: ref,
            mode: wireMode,
            notes: wrap.querySelector('#sd-b2b-po-notes').value,
            poDoc: dataUrl ? { filename: file.name, data: dataUrl } : null
          })
        });
      })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error(res.j.message || 'Could not submit that PO.');
        var j = res.j;

        out.className = 'sd-b2b-msg good';
        out.innerHTML = j.mode === 'reserve'
          ? '<strong>PO ' + ref + ' reserved.</strong> Nothing has been ordered. We will come back to you shortly to confirm stock, freight and timing. Your copy is on its way by email.'
          : j.mode === 'held'
            ? '<strong>PO ' + ref + ' received.</strong> We are confirming a couple of lines before it becomes an order. Your copy is on its way by email.'
            : '<strong>PO ' + ref + ' confirmed as ' + j.order.name + '.</strong> Net 30 from the invoice date. Your copy is on its way by email.';

        btn.querySelector('.t').textContent = 'Submitted';
        // The cart has become a PO, so clear it and move them to the portal.
        fetch('/cart/clear.js', { method: 'POST' }).then(function () {
          setTimeout(function () { window.location.href = EP + '/portal'; }, 3000);
        });
      })
      .catch(function (e) {
        fail(e.message);
        Array.prototype.forEach.call(buttons, function (b) { b.disabled = false; });
        btn.querySelector('.t').textContent = originalLabel;
      });
  }

  // Trade carts go to the FULL cart page, never the drawer. Three actions, a PO
  // number, a date, notes and a file picker do not fit in a side drawer, and a
  // trade buyer needs to see the whole order before committing to Net 30.
  // Trade buyers get their own cart page rather than the retail one. Reshaping
  // the retail cart with CSS was fragile and never read like a purchase order.
  function redirectRetailCart() {
    if (/^\/cart\/?$/.test(window.location.pathname)) {
      window.location.replace(EP + '/cart');
      return true;
    }
    return false;
  }

  function forceFullCart() {
    var TOGGLES = '#cart-icon-bubble, a[href="/cart"], a[href^="/cart?"], .header__icon--cart, [data-cart-drawer-toggle], cart-drawer-toggle';

    // Capture phase, so this runs before the theme's own drawer handler.
    document.addEventListener('click', function (e) {
      var el = e.target && e.target.closest ? e.target.closest(TOGGLES) : null;
      if (!el) return;
      if (el.closest('.sd-b2b-po')) return;
      e.preventDefault();
      e.stopPropagation();
      window.location.href = EP + '/cart';
    }, true);

    // Themes also pop the drawer open programmatically after an add-to-cart.
    // The CSS hides it outright in trade mode; this stops it stealing focus.
    document.addEventListener('cart:open', function (e) { e.preventDefault && e.preventDefault(); }, true);
  }

  // Mounts the PO panel on the cart. Deliberately stubborn: the cart section
  // re-renders asynchronously and themes move the checkout button around, so a
  // single attempt against a single selector was too fragile. If trade mode is
  // on and this still cannot mount, it SAYS SO on the page rather than leaving
  // a silent retail cart, which is exactly how the original failure looked.
  var mountTries = 0;

  function onCartPage() {
    return /\/cart(\/|$|\?)/.test(window.location.pathname + window.location.search)
      || !!document.querySelector('form[action*="/cart"], .cart__footer, #main-cart-footer');
  }

  // Never mount inside the cart DRAWER. Trade mode hides the drawer entirely,
  // and #CartDrawer-Checkout lives inside it, so matching that selector put the
  // whole panel inside a display:none element: prices rewrote, the checkout
  // button disappeared, and the panel was invisible. That was the bug.
  function inHiddenDrawer(el) {
    return !!(el && el.closest && el.closest('cart-drawer, #CartDrawer, .cart-drawer, cart-notification, #CartNotification'));
  }

  function findHost() {
    // Prefer the real cart PAGE containers, in order of preference.
    var page = document.querySelector('#main-cart-footer')
      || document.querySelector('.cart__footer')
      || document.querySelector('#main-cart form[action*="/cart"]')
      || document.querySelector('#main-cart-footer .cart__blocks')
      || document.querySelector('form[action*="/cart"]');
    if (page && !inHiddenDrawer(page)) return page;

    // Only then fall back to a checkout button, and only one on the page.
    var buttons = document.querySelectorAll('[name="checkout"], .cart__checkout-button, #checkout, .cart__ctas');
    for (var i = 0; i < buttons.length; i++) {
      if (inHiddenDrawer(buttons[i])) continue;
      var host = buttons[i].closest('.cart__footer, .cart__blocks, form');
      if (host && !inHiddenDrawer(host)) return host;
      if (!inHiddenDrawer(buttons[i].parentNode)) return buttons[i].parentNode;
    }
    return null;
  }

  // A visible way into the trade portal from every page, sitting with the
  // header icons where a customer already looks for their account.
  // Blasters and the slicer profiles are consumer lines. Trade accounts should
  // not see them in the nav, and should not be able to browse or order them.
  // Nav links go by href so a menu rename cannot silently un-hide them.
  var B2C_PATHS = [
    '/collections/3d-blasters',
    '/collections/out-of-darts',
    '/collections/premade-printed-blasters',
    '/pages/3d-printable-nerf-blasters',
    '/pages/slicer-profiles'
  ];

  function isB2cPath(href) {
    if (!href) return false;
    var path = href.replace(/^https?:\/\/[^/]+/, '').split('?')[0].replace(/\/$/, '');
    for (var i = 0; i < B2C_PATHS.length; i++) {
      if (path === B2C_PATHS[i] || path.indexOf(B2C_PATHS[i] + '/') === 0) return true;
    }
    return false;
  }

  function hideB2cLinks(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var links = scope.querySelectorAll('a[href]');
    Array.prototype.forEach.call(links, function (a) {
      if (!isB2cPath(a.getAttribute('href'))) return;
      // hide the whole nav item, not just the anchor
      var item = a.closest('li, .header__menu-item, .mega-menu__list > li') || a;
      item.style.setProperty('display', 'none', 'important');
      item.setAttribute('data-sd-b2b-hidden', '');
    });
  }

  // If they land on one of those pages directly, send them to the catalogue.
  function blockB2cPages() {
    if (!isB2cPath(window.location.pathname)) return false;
    window.location.replace('/collections/all');
    return true;
  }

  function mountPortalLink() {
    var mobilePill = !!(window.SD_B2B && window.SD_B2B.portalPillOnMobile);
    if (!document.getElementById('sd-b2b-portal-link')) {
      var icons = document.querySelector('.header__icons, .header__icon--account')
        && (document.querySelector('.header__icons') || document.querySelector('.header__icon--account').parentNode);
      if (icons) {
        var a = document.createElement('a');
        a.id = 'sd-b2b-portal-link';
        // Without portalPillOnMobile the pill is desktop/tablet only; the CSS
        // hides it below 750px and the menu-drawer link below takes over.
        a.className = 'sd-b2b-portal-link' + (mobilePill ? '' : ' sd-b2b-desktop-only');
        a.href = EP + '/portal';
        a.innerHTML = '<span class="sd-b2b-dot"></span><span class="sd-b2b-label">Trade portal</span>';
        var account = icons.querySelector('.header__icon--account');
        if (account) icons.insertBefore(a, account); else icons.appendChild(a);
      }
    }
    if (!mobilePill) mountDrawerPortalLink();
  }

  /* Mobile home for the portal entry: a plain utility link in the menu drawer,
     next to Log in. medium-hide/large-up-hide are the theme's own classes on
     the drawer account link, so this shows exactly where that does (<750px). */
  function mountDrawerPortalLink() {
    if (document.getElementById('sd-b2b-drawer-link')) return;
    var util = document.querySelector('.menu-drawer__utility-links');
    if (!util) return;
    var a = document.createElement('a');
    a.id = 'sd-b2b-drawer-link';
    a.className = 'sd-b2b-drawer-link menu-drawer__account link focus-inset h5 medium-hide large-up-hide';
    a.href = EP + '/portal';
    a.innerHTML = '<span class="sd-b2b-dot"></span>Trade portal';
    var account = util.querySelector('.menu-drawer__account');
    if (account && account.parentNode === util) util.insertBefore(a, account.nextSibling);
    else util.insertBefore(a, util.firstChild);
  }

  function mountPoBuilder() {
    if (document.querySelector('.sd-b2b-po')) { stripCheckout(document); return; }
    if (!onCartPage()) return;

    var host = findHost();
    if (!host) {
      if (++mountTries < 12) { setTimeout(mountPoBuilder, 400); return; }
      console.warn('[sd-b2b] no cart container found');
      return;
    }

    // MOUNT FIRST, FILL SECOND.
    // The panel used to appear only after /api/price succeeded, so any failure
    // there left a silent retail cart with no explanation. Now the panel is on
    // the page immediately and reports its own state, whatever happens next.
    var shell = document.createElement('div');
    shell.className = 'sd-b2b-po';
    shell.setAttribute('data-sd-b2b-skip', '');
    shell.innerHTML =
      '<h3>Purchase order</h3>' +
      '<p class="sd-b2b-sub">Loading your trade pricing</p>' +
      '<div class="sd-b2b-msg" id="sd-b2b-boot"></div>';
    host.appendChild(shell);
    stripCheckout(document);
    console.info('[sd-b2b] PO panel shell mounted');

    var boot = shell.querySelector('#sd-b2b-boot');

    cartJson()
      .then(function (cart) {
        if (!cart.items || !cart.items.length) {
          shell.innerHTML = '<h3>Purchase order</h3>' +
            '<div class="sd-b2b-msg">Your cart is empty. Add items and they will appear here as a purchase order.</div>';
          return null;
        }
        return priceCart(cart.items.map(function (i) { return { variantId: i.variant_id, qty: i.quantity }; }))
          .then(function (priced) {
            if (!priced || priced.error) {
              throw new Error(priced && priced.message ? priced.message : (priced && priced.error) || 'pricing unavailable');
            }
            var panel = buildPanel(cart, priced);
            shell.parentNode.replaceChild(panel, shell);
            rewriteCartTotals(priced.totals);
            stripCheckout(document);
            console.info('[sd-b2b] PO builder ready with', cart.items.length, 'line(s)');
            return true;
          });
      })
      .catch(function (e) {
        console.warn('[sd-b2b] PO builder could not load:', e.message);
        shell.querySelector('.sd-b2b-sub').textContent = 'Something went wrong';
        boot.className = 'sd-b2b-msg bad';
        boot.textContent = 'We could not load your trade pricing: ' + e.message;
        var help = document.createElement('div');
        help.className = 'sd-b2b-terms';
        help.innerHTML = 'Reload the page, or submit your PO from <a href="' + EP + '/portal">your trade portal</a>.';
        shell.appendChild(help);
      });
  }

  // ---------- boot ----------

  // Cached per visit: the script now loads for every visitor, so this must not
  // become one request per page view.
  function loadSession() {
    var KEY = 'sd-b2b-session-v1';
    // A POSITIVE result is stable, so cache it for a while. A NEGATIVE result is
    // not: the visitor may be about to sign in, and caching "not a trade
    // account" for ten minutes would leave them stuck on retail pricing until
    // it expired. Negatives get a short life, and signing in clears it outright.
    var POSITIVE_TTL = 10 * 60 * 1000;
    var NEGATIVE_TTL = 45 * 1000;

    if (/^\/account/.test(window.location.pathname)) {
      try { sessionStorage.removeItem(KEY); } catch (e) { /* fine */ }
    }

    try {
      var raw = sessionStorage.getItem(KEY);
      if (raw) {
        var hit = JSON.parse(raw);
        var ttl = (hit && hit.s && hit.s.trade) ? POSITIVE_TTL : NEGATIVE_TTL;
        if (hit && Date.now() - hit.at < ttl) return Promise.resolve(hit.s);
      }
    } catch (e) { /* private mode, just fetch */ }

    return fetch(EP + '/api/session', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (s) {
        try { sessionStorage.setItem(KEY, JSON.stringify({ at: Date.now(), s: s })); } catch (e) { /* fine */ }
        return s;
      });
  }

  loadSession()
    .then(function (s) {
      // The app is the authority on whether this really is a live trade account.
      if (!s || !s.trade) {
        // Clear the pre-paint hint so a lapsed or signed-out account stops
        // hiding consumer nav on the next load.
        try { localStorage.removeItem('sd-b2b-trade'); } catch (e) { /* fine */ }
        document.documentElement.classList.remove('sd-b2b-on', 'sd-b2b-hide-b2c');
        console.warn('[sd-b2b] trade mode OFF —', s && s.signedIn
          ? 'signed in but account status is "' + (s.status || 'none') + '", not approved'
          : 'not signed in on the storefront');
        return;
      }
      // Remembered for the next page load so the nav never paints then reflows.
      try { localStorage.setItem('sd-b2b-trade', '1'); } catch (e) { /* fine */ }
      document.documentElement.classList.add('sd-b2b-hide-b2c');
      console.info('[sd-b2b] trade mode ON —', s.tier, Math.round(s.discount * 100) + '% off, prices ex GST');
      session = s;

      document.documentElement.classList.add('sd-b2b-on');
      document.body.classList.add('sd-b2b-on');

      // Fill the banner from the VERIFIED session, not from Liquid, so the
      // rate on screen is always the rate the account actually gets.
      var rate = document.getElementById('sd-b2b-rate');
      var tierEl = document.getElementById('sd-b2b-tiername');
      if (rate) {
        rate.textContent = s.pricedOffBook
          ? 'Reseller pricing, ex GST'
          : s.discount > 0
            ? Math.round(s.discount * 100) + '% off retail'
            : 'All prices ex GST';
      }
      if (tierEl) tierEl.textContent = s.tier;

      // Straight away, not after the book fetch — the bar is the first thing on
      // screen and it must not flash the retail marquee.
      resellerMarquee(document);
      interceptAddToCart();
      unlockSoldOut(document);

      // The book has to be in hand BEFORE the first sweep, or a reseller sees
      // retail ex GST for a moment and then watches it drop.
      loadBook().then(function () {
        sweep(document);
        if (onBook()) {
          console.info('[sd-b2b] reseller price book loaded —', book ? Object.keys(book).length : 0, 'priced products');
        }
      });
      watch();
      if (blockB2cPages()) return;
      hideB2cLinks(document);
      mountPortalLink();
      if (redirectRetailCart()) return;
      forceFullCart();
      // The cart section replaces its own DOM on quantity changes, which throws
      // the panel away. Re-mount on every signal the theme gives us.
      ['cart:updated', 'cart:refresh', 'sd:cart:changed'].forEach(function (ev) {
        document.addEventListener(ev, function () { setTimeout(mountPoBuilder, 150); });
      });
      window.addEventListener('load', function () { setTimeout(mountPoBuilder, 300); });
    })
    .catch(function (e) {
      // Stay retail, but say why. Silent failure here is what made this look
      // like it simply was not working.
      console.warn('[sd-b2b] could not reach ' + EP + '/api/session, staying on retail pricing:', e.message);
    });
})();
